import {
  getCountFromServer,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from "firebase/firestore";

import { PROMOTION_SCOPES } from "../config/promotion_settings_map";
import { PROMOTION_WORKFLOW, getPromotionWorkflowState } from "../helpers/promotion_workflow";
import { getPromotionIdeasCollection, getPromotionPostsCollection } from "./promotion_repository";

const scopeConstraint = (eventId) => eventId
  ? where("eventId", "==", eventId)
  : where("scope", "==", PROMOTION_SCOPES.INTERNAL_LGV);

// Include undrafted and legacy undated Ideas. Two scoped reads avoid per-Idea
// queries and arbitrary page limits that would undercount saved variations.
export const getPromotionIdeaDashboard = async ({ eventId = null } = {}) => {
  const [ideas, posts] = await Promise.all([
    getDocs(query(getPromotionIdeasCollection(), scopeConstraint(eventId))),
    getDocs(query(getPromotionPostsCollection(), scopeConstraint(eventId))),
  ]);
  const counts = new Map();
  const timestamp = (value) => {
    const date = typeof value?.toDate === "function" ? value.toDate() : new Date(value || 0);
    return Number.isNaN(date.getTime()) ? 0 : date.getTime();
  };
  posts.docs.forEach((record) => {
    const post = record.data();
    if (!post.ideaId) return;
    const value = counts.get(post.ideaId) || { draftCount: 0, promotionCount: 0, postsByPlatform: {} };
    value.promotionCount += 1;
    if (getPromotionWorkflowState(post) === PROMOTION_WORKFLOW.DRAFT && !["rejected", "archived"].includes(post.queueStatus)) value.draftCount += 1;
    const previous = value.postsByPlatform[post.platform];
    if (post.platform && (!previous || timestamp(post.updatedAt || post.createdAt) > timestamp(previous.updatedAt))) {
      // Match the Idea Bank's newest-post-per-platform badge without returning
      // another post row or fetching its media assets.
      value.postsByPlatform[post.platform] = {
        id: record.id, platform: post.platform, queueStatus: post.queueStatus || "draft",
        publishStatus: post.publishStatus || "", updatedAt: post.updatedAt || post.createdAt || null,
        scheduledAt: post.scheduledAt || null, publishedAt: post.publishedAt || null, postedAt: post.postedAt || null,
      };
    }
    counts.set(post.ideaId, value);
  });
  return ideas.docs.map((record) => ({ ...record.data(), id: record.id, ...(counts.get(record.id) || { draftCount: 0, promotionCount: 0, postsByPlatform: {} }) }))
    .sort((left, right) => timestamp(right.updatedAt || right.createdAt) - timestamp(left.updatedAt || left.createdAt) || left.id.localeCompare(right.id));
};

const count = async (collectionRef, constraints) => {
  const snapshot = await getCountFromServer(query(collectionRef, ...constraints));
  return snapshot.data().count;
};

export const getPromotionWorkflowCounts = async ({ eventId = null } = {}) => {
  const scope = scopeConstraint(eventId);
  const postsCollection = getPromotionPostsCollection();

  const [drafts, approved, scheduled, posting, published, posted, failed] = await Promise.all([
    count(postsCollection, [scope, where("queueStatus", "==", "draft")]),
    count(postsCollection, [scope, where("queueStatus", "==", "approved"), where("publishStatus", "==", "not_ready")]),
    count(postsCollection, [scope, where("publishStatus", "==", "scheduled")]),
    count(postsCollection, [scope, where("publishStatus", "==", "posting")]),
    count(postsCollection, [scope, where("publishStatus", "==", "published")]),
    count(postsCollection, [scope, where("publishStatus", "==", "posted")]),
    count(postsCollection, [scope, where("publishStatus", "==", "failed")]),
  ]);

  return {
    draft: drafts,
    approved,
    scheduled: scheduled + posting,
    published: published + posted,
    failed,
  };
};

const workflowConstraints = (workflow) => {
  switch (workflow) {
    case PROMOTION_WORKFLOW.IDEA:
      return [where("ideaStatus", "==", "new")];
    case PROMOTION_WORKFLOW.DRAFT:
      return [where("queueStatus", "==", "draft")];
    case PROMOTION_WORKFLOW.APPROVED:
      return [where("queueStatus", "==", "approved"), where("publishStatus", "==", "not_ready")];
    case PROMOTION_WORKFLOW.SCHEDULED:
      return [where("publishStatus", "in", ["scheduled", "posting"])];
    case PROMOTION_WORKFLOW.PUBLISHED:
      return [where("publishStatus", "in", ["published", "posted"])];
    case PROMOTION_WORKFLOW.FAILED:
      return [where("publishStatus", "==", "failed")];
    default:
      throw new Error(`Unknown promotion workflow: ${workflow}`);
  }
};

export const getPromotionWorkflowPage = async ({
  eventId = null,
  workflow,
  pageSize = 25,
  cursor = null,
}) => {
  const collectionRef = workflow === PROMOTION_WORKFLOW.IDEA
    ? getPromotionIdeasCollection()
    : getPromotionPostsCollection();
  const constraints = [
    scopeConstraint(eventId),
    ...workflowConstraints(workflow),
    orderBy("updatedAt", "desc"),
  ];

  if (cursor) constraints.push(startAfter(cursor));
  constraints.push(limit(pageSize + 1));

  const snapshot = await getDocs(query(collectionRef, ...constraints));
  const pageDocuments = snapshot.docs.slice(0, pageSize);

  return {
    rows: pageDocuments.map((document) => ({ id: document.id, ...document.data() })),
    cursor: pageDocuments[pageDocuments.length - 1] || null,
    hasMore: snapshot.docs.length > pageSize,
  };
};
